import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Box, Card, Toggle } from '@nimbus-ds/components';
import {
  OrdersAutoCancelExpirationTime,
  OrdersAutoCancelPaymentStatusConfiguration,
} from '@tiendanube/common/src/domains/orders/orders';
import {
  OrdersAutoCancelPaymentMethod,
  OrdersAutoCancelPaymentStatus,
} from '@tiendanube/common/src/enums';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import PaymentMethodConfig from './PaymentMethodConfig';

interface PaymentStatusConfigProps {
  paymentStatus: OrdersAutoCancelPaymentStatus;
  enabledPaymentMethods: OrdersAutoCancelPaymentMethod[];
  configuration: OrdersAutoCancelPaymentStatusConfiguration;
  disabled: boolean;
  onChange: (
    paymentStatus: OrdersAutoCancelPaymentStatus,
    configuration: OrdersAutoCancelPaymentStatusConfiguration,
  ) => void;
  paymentMethodsWithError: OrdersAutoCancelPaymentMethod[];
}

function PaymentStatusConfig({
  paymentStatus,
  enabledPaymentMethods,
  configuration,
  disabled,
  onChange,
  paymentMethodsWithError = [],
}: PaymentStatusConfigProps): JSX.Element {
  const t = useTranslationOrders('autoCancel');

  const [paymentStatusConfig, setPaymentStatusConfig] =
    useState<OrdersAutoCancelPaymentStatusConfiguration>(configuration);

  useEffect(() => {
    setPaymentStatusConfig(configuration);
  }, [configuration]);

  const handleToggleChange = (
    e: ChangeEvent,
    paymentMethod: OrdersAutoCancelPaymentMethod,
    isActive: boolean,
  ) => {
    if (disabled) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    const newConfig = {
      ...paymentStatusConfig,
      [paymentMethod]: {
        ...paymentStatusConfig[paymentMethod],
        isActive,
      },
    };
    setPaymentStatusConfig(newConfig);
    onChange(paymentStatus, newConfig);
  };

  const handleExpirationTimeChange = (
    paymentMethod: OrdersAutoCancelPaymentMethod,
    value: number,
  ) => {
    const newConfig = {
      ...paymentStatusConfig,
      [paymentMethod]: {
        ...paymentStatusConfig[paymentMethod],
        expirationTime: value as OrdersAutoCancelExpirationTime,
      },
    };
    setPaymentStatusConfig(newConfig);
    onChange(paymentStatus, newConfig);
  };

  const paymentMethodsWithErrorSet = useMemo(() => new Set(paymentMethodsWithError), [paymentMethodsWithError]);

  return (
    <Card>
      <Card.Header title={t(`paymentStatus.${paymentStatus}`)} />
      <Card.Body>
        {enabledPaymentMethods.map((paymentMethod, index) => (
          <Box
            paddingBottom={
              index === enabledPaymentMethods.length - 1 ? 'none' : '4'
            }
            key={`box-${paymentStatus}-${paymentMethod}`}
            gap="2"
          >
            <Toggle
              name={`paymentMethodToggle-${paymentStatus}-${paymentMethod}`}
              label={t(`paymentMethod.${paymentMethod}`)}
              checked={paymentStatusConfig[paymentMethod]?.isActive}
              onChange={(e) => {
                handleToggleChange(
                  e,
                  paymentMethod,
                  !paymentStatusConfig[paymentMethod]?.isActive,
                );
              }}
            />
            {paymentStatusConfig[paymentMethod]?.isActive && (
              <PaymentMethodConfig
                paymentStatus={paymentStatus}
                paymentMethod={paymentMethod}
                expirationTime={
                  paymentStatusConfig[paymentMethod]?.expirationTime || null
                }
                onExpirationTimeChange={handleExpirationTimeChange}
                hasError={paymentMethodsWithErrorSet.has(paymentMethod)}
              />
            )}
          </Box>
        ))}
      </Card.Body>
    </Card>
  );
}

export default PaymentStatusConfig;
